// Configuration API
const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_EP_URI;

export interface VerifyTicketResult {
  status: string;
  user_id: number;
  login: string;
  firstname: string | null;
  lastname: string | null;
  td_group: string;
  access_token: string;
  refresh_token: string;
}

export type GroupState =
  | 'available'
  | 'locked_other'
  | 'locked_by_you_solo'
  | 'awaiting_your_validation'
  | 'pending_validation'
  | 'complete';

export interface GroupMember {
  user_id: number;
  firstname: string | null;
  lastname: string | null;
}

export interface GroupStatus {
  id: number;
  generic_name: string | null;
  id_group: number | null;
  state: GroupState;
  lock_expires_at: string | null;
  members: GroupMember[];
  candidate: { firstname: string | null; lastname: string | null } | null;
}

export type MyState =
  | 'no_selection'
  | 'locked_by_you_solo'
  | 'awaiting_your_validation'
  | 'pending_validation'
  | 'complete';

export interface SelectionStatus {
  td_group: string;
  can_be_solo: boolean;
  my_state: MyState;
  my_group_id: number | null;
  groups: GroupStatus[];
}

class ProjectGroupSelectionService {

  async getCasLoginUrl(): Promise<string> {
    const response = await fetch(`${API_BASE_URL}/project-group-selection/cas-login-url`);
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }
    return json.url;
  }

  async verifyTicket(ticket: string): Promise<VerifyTicketResult> {
    const response = await fetch(`${API_BASE_URL}/project-group-selection/verify-ticket?ticket=${encodeURIComponent(ticket)}`);
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }
    return json;
  }

  // Contournement du CAS pour tester en local (voir ProjectGroupSelectionDevLoginResource,
  // FLASK_ENV=development uniquement — le backend renvoie 403 en dehors de ce contexte).
  async devLogin(login: string): Promise<VerifyTicketResult> {
    const response = await fetch(`${API_BASE_URL}/project-group-selection/dev-login?login=${encodeURIComponent(login)}`);
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }
    return json;
  }

  private async authedCall(path: string, accessToken: string, body?: any): Promise<SelectionStatus> {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method: body !== undefined ? 'POST' : 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json.message || `HTTP error! status: ${response.status}`);
    }
    return json;
  }

  async getStatus(accessToken: string): Promise<SelectionStatus> {
    return this.authedCall('/project-group-selection/status', accessToken);
  }

  async chooseGroup(groupId: number, accessToken: string): Promise<SelectionStatus> {
    return this.authedCall('/project-group-selection/choose', accessToken, { group_id: groupId });
  }

  async cancelChoice(accessToken: string): Promise<SelectionStatus> {
    return this.authedCall('/project-group-selection/cancel', accessToken, {});
  }

  async validateCandidate(accessToken: string): Promise<SelectionStatus> {
    return this.authedCall('/project-group-selection/validate', accessToken, {});
  }
}

export const projectGroupSelectionService = new ProjectGroupSelectionService();
